import React, {useEffect, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import {FormInput, FormInputContainer} from '../../../shared/form/form-control/FormInput';
import {Btn} from '../../../shared/elements/btn/Btn';
import {FormDatePicker} from '../../../shared/form/form-control/FormDatePicker';
import {FormSelect} from '../../../shared/form/form-control/FormSelect';
import {updateExtraWorkEntry} from './endpoints';
import errorHandler from '../../../util/errorHandler';
import {validationSchema, validateValues} from './helpers';
import {Icon} from '../../../shared/elements/icon/Icon';
import {ValidationErrorTxt} from '../../../shared/form/form-features/ValidationErrorTxt';

import {Row} from '../../../shared/grid/grid-features/Row';
import {Col, ColContainer} from '../../../shared/grid/grid-features/Col';
import styled from 'styled-components';
import {FormLabel} from '../../../shared/form/form-features/FormLabel';
import {FormGroup} from '../../../shared/form/form-features/FormGroup';

const setTotalPrice = (quantity, setFieldValue, pricePerUnit) => {
    let quantityAsFloat = parseFloat(quantity);
    let pricePerUnitAsFloat = parseFloat(pricePerUnit);
    if (!isNaN(quantityAsFloat) && !isNaN(pricePerUnitAsFloat)) {
        setFieldValue('totalPrice', pricePerUnitAsFloat * quantityAsFloat);
    } else {
        setFieldValue('totalPrice', '');
    }
};

function EditExtraWorkEntry({extraWorkEntry, customer, onSave, onCancel, t}) {
    const [workDate, setWorkDate] = useState(new Date(extraWorkEntry.timestamp));
    const [selectedExtraWorkItem, setSelectedExtraWorkItem] = useState();

    useEffect(() => {
        setWorkDate(new Date(extraWorkEntry.timestamp));
    }, [extraWorkEntry.id]);

    const handleSelectExtraWorkItem = (ewiId, setFieldValue, setValues, values) => {
        let ewiIdAsInt = parseInt(ewiId, 10);

        let selectedEWItem = _.find(customer.extraWorkItems, {id: ewiIdAsInt});
        if (selectedEWItem) {
            setFieldValue('extraWorkItemId', ewiId);
            setFieldValue('unit', selectedEWItem.unit);
            setFieldValue('pricePerUnit', selectedEWItem.pricePerUnit);

            setTotalPrice(values.quantity, setFieldValue, selectedEWItem.pricePerUnit);
            setSelectedExtraWorkItem(selectedEWItem);
        } else {
            setValues((prev) => ({...prev, extraWorkItemId: '', unit: '', pricePerUnit: '', totalPrice: ''}));
            setSelectedExtraWorkItem();
        }
    };

    const handleQuantityChange = (quantity, setFieldValue, values) => {
        setFieldValue('quantity', quantity);
        setTotalPrice(quantity, setFieldValue, values.pricePerUnit);
    };

    const handleSubmit = (values, actions) => {
        updateExtraWorkEntry(validateValues(values))
            .then(() => onSave())
            .catch((e) => errorHandler(e.response.data, true, t));

        setTimeout(() => {
            actions.setSubmitting(false);
        }, 500);
    };
    return (
        <div>
            <Formik
                initialValues={{...extraWorkEntry, timestamp: new Date(extraWorkEntry.timestamp)}}
                onSubmit={(values, actions) => handleSubmit(values, actions)}
                validationSchema={validationSchema(t, selectedExtraWorkItem)}
                validateOnChange
                enableReinitialize
            >
                {({isSubmitting, values, setFieldValue, setFieldTouched, setValues}) => (
                    <Form>
                        <Wrapper>
                            <Col colsize="25">
                                <div>
                                    <FormDatePicker
                                        label={t('customer.ExtraWorkEntries.Date')}
                                        name="timestamp"
                                        selected={workDate}
                                        onChange={(date) => {
                                            setWorkDate(date);
                                            setFieldValue('timestamp', date ?? '');
                                        }}
                                        onBlur={() => {
                                            setFieldTouched('timestamp', true);
                                        }}
                                        dateFormat="dd-MM-yyyy"
                                        calendarStartDay={1}
                                        placeholderText={t('customer.ExtraWorkEntries.SelectDate')}
                                    />
                                </div>
                            </Col>

                            <Col colsize="25">
                                <FormSelect
                                    label={t('customer.ExtraWorkEntries.ExtraWorkItem')}
                                    onChange={(evt) => {
                                        handleSelectExtraWorkItem(evt.target.value, setFieldValue, setValues, values);
                                    }}
                                    onBlur={() => setFieldTouched('extraWorkItemId', true)}
                                    defaultValue={extraWorkEntry.extraWorkItemId}
                                >
                                    <option key={0} value="0">
                                        {t('customer.SelectExtraWorkItem')}
                                    </option>
                                    {customer.extraWorkItems?.length > 0 &&
                                        customer.extraWorkItems.map((ewi, index) => {
                                            return (
                                                <option value={ewi.id} key={index + 1}>
                                                    {ewi.description}
                                                </option>
                                            );
                                        })}
                                </FormSelect>
                                <ErrorMessage name="extraWorkItemId">
                                    {(msg) => <ValidationErrorTxt>{msg}</ValidationErrorTxt>}
                                </ErrorMessage>
                            </Col>

                            <Col colsize="25">
                                <FormLabel>{t('customer.ExtraWorkEntries.Quantity')}</FormLabel>
                                <FormInputContainer>
                                    <Field
                                        name="quantity"
                                        placeholder="0"
                                        onChange={(ev) => {
                                            handleQuantityChange(ev.target.value, setFieldValue, values);
                                        }}
                                    ></Field>
                                </FormInputContainer>
                                <ErrorMessage name="quantity">
                                    {(msg) => <ValidationErrorTxt>{msg}</ValidationErrorTxt>}
                                </ErrorMessage>
                            </Col>

                            <Col colsize="25">
                                <FormInput
                                    label={t('customer.ExtraWorkEntries.Unit')}
                                    name="unit"
                                    value={values.unit}
                                    disabled={true}
                                />
                            </Col>
                            <Col colsize="25">
                                <FormInput
                                    label={t('customer.ExtraWorkEntries.PricePerItem')}
                                    name="pricePerUnit"
                                    value={values.pricePerUnit}
                                    disabled={true}
                                />
                            </Col>

                            <Col colsize="25">
                                <FormInput
                                    label={t('customer.ExtraWorkEntries.TotalPrice')}
                                    name="totalPrice"
                                    value={values.totalPrice}
                                    disabled={true}
                                />
                            </Col>

                            <Col colsize="25">
                                <FormInput label={t('customer.ExtraWorkEntries.Remark')} name="remark" />
                            </Col>
                        </Wrapper>
                        <Btn type="submit" color="success" disabled={isSubmitting}>
                            {t('customer.EditExtraWork')}
                            <Icon icon="upgrade" />
                        </Btn>
                        <Btn color="danger" disabled={isSubmitting} onClick={onCancel}>
                            {t('general.Cancel')}
                        </Btn>
                    </Form>
                )}
            </Formik>
        </div>
    );
}

export default withTranslation()(EditExtraWorkEntry);

const Wrapper = styled(Row)`
    margin-top: 12px;
    ${ColContainer} {
        margin-bottom: 16px;
    }
    ${FormGroup} {
        margin-bottom: 0;
    }
`;
